import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Loader } from 'core-components/library/loader'
import { useMdfActivitiesRefresh } from 'partner360/features/mdfActivities/hooks/useMdfActivitiesRefresh'
import { selectMdfActivitiesState } from 'partner360/features/mdfActivities/model/selectors/selectMdfActivitiesState'
import { MdfActivitiesSummaryTable } from 'partner360/features/mdfActivities/ui/mdfActivitiesSummaryTable/MdfActivitiesSummaryTable'

import { useKeyInitiativeMdfMetrics } from '../../model/hooks/useKeyInitiativeMdfMetrics'
import { TRANSLATION_KEYS } from '../../translation'

import { HandHoldingDollarIcon } from './HandHoldingDollarIcon'
import { KeyInitiativeMdfSummary } from './KeyInitiativeMdfSummary'

import styles from './KeyInitiativeMdfSection.module.scss'

const KEYS = TRANSLATION_KEYS.keyInitiativeEditor.mdfFieldGroup

interface IKeyInitiativeMdfSectionProps {
  initiativeId?: number
  partnerId?: string
  fYear: number
  programType: ProgramTypes
  currency: string
  period: readonly string[]
  businessUnit: string
  focusArea: string
}

export const KeyInitiativeMdfSection = ({
  initiativeId,
  partnerId,
  fYear,
  programType,
  currency,
  period,
  businessUnit,
  focusArea,
}: IKeyInitiativeMdfSectionProps) => {
  const hasSavedInitiative = initiativeId != null && initiativeId > 0 && Boolean(partnerId)
  const { records, error } = useSelector(selectMdfActivitiesState)
  const { isRefreshing, isLoadedForCurrentContext, loadMdfActivities } = useMdfActivitiesRefresh(
    partnerId ?? '',
    { fYear, programType, skip: !hasSavedInitiative }
  )
  const activities = useMemo(
    () =>
      hasSavedInitiative && isLoadedForCurrentContext
        ? records.filter(
            (activity) =>
              activity.keyInitiativeId === initiativeId && activity.partnerId === partnerId
          )
        : [],
    [hasSavedInitiative, initiativeId, isLoadedForCurrentContext, partnerId, records]
  )

  const metrics = useKeyInitiativeMdfMetrics({
    activities,
    partnerId: partnerId ?? '',
    fYear,
    programType,
    period,
    businessUnit,
    focusArea,
  })

  return (
    <section className={styles.root}>
      <header className={styles.header}>
        <span className={styles.icon}>
          <HandHoldingDollarIcon />
        </span>
        <span className={styles.title}>
          <Translation text={KEYS.title} span />
        </span>
      </header>
      {!hasSavedInitiative ? (
        <div className={styles.message}>
          <Translation text={KEYS.unsaved} />
        </div>
      ) : error && !isRefreshing ? (
        <div>
          <InfoBlock type={InfoBlockType.Warning} text={<Translation text={KEYS.loadError} />} />
          <Button variant="text" size="sm" onClick={() => void loadMdfActivities()}>
            <Translation text={KEYS.retry} />
          </Button>
        </div>
      ) : !isLoadedForCurrentContext ? (
        <Loader />
      ) : (
        <div className={styles.tableContainer}>
          <LoaderOverlay isVisible={isRefreshing} />
          {activities.length > 0 ? (
            <>
              <KeyInitiativeMdfSummary {...metrics} />
              <MdfActivitiesSummaryTable activities={activities} currency={currency} />
            </>
          ) : (
            <div className={styles.message}>
              <Translation text={KEYS.empty} />
            </div>
          )}
        </div>
      )}
    </section>
  )
}
